import React, { useState, useMemo } from 'react';
import { X, Plus, AlertTriangle, TrendingUp, Target, Zap } from 'lucide-react';
import { DependencyGraphNode } from '../../types';
import { apiService } from '../../services/api';

interface CreateDependencyModalProps {
  availableGoals: DependencyGraphNode[];
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateDependencyModal: React.FC<CreateDependencyModalProps> = ({
  availableGoals,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    sourceGoalId: '',
    targetGoalId: '',
    dependencyType: 'blocks' as 'blocks' | 'enables' | 'reduces_complexity' | 'accelerates',
    strength: 3,
    description: '',
    impactPercentage: 50
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Group goals by project for better organization
  const goalsByProject = useMemo(() => {
    const grouped = availableGoals.reduce((acc, goal) => {
      if (!acc[goal.projectName]) {
        acc[goal.projectName] = [];
      }
      acc[goal.projectName].push(goal);
      return acc;
    }, {} as Record<string, DependencyGraphNode[]>);

    // Sort goals within each project by level
    Object.keys(grouped).forEach(project => {
      grouped[project].sort((a, b) => a.level - b.level);
    });

    return grouped;
  }, [availableGoals]);

  const getDependencyIcon = (type: string) => {
    switch (type) {
      case 'blocks':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'enables':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'reduces_complexity':
        return <Target className="w-4 h-4 text-yellow-500" />;
      case 'accelerates':
        return <Zap className="w-4 h-4 text-blue-500" />;
      default:
        return null;
    }
  };

  const getDependencyDescription = (type: string) => {
    switch (type) {
      case 'blocks':
        return 'Source goal must be completed before target goal can start';
      case 'enables':
        return 'Source goal completion makes target goal possible';
      case 'reduces_complexity':
        return 'Source goal simplifies the target goal implementation';
      case 'accelerates':
        return 'Source goal speeds up target goal completion';
      default:
        return '';
    }
  };

  const getStrengthLabel = (strength: number) => {
    const labels = ['', 'Very Weak', 'Weak', 'Moderate', 'Strong', 'Very Strong'];
    return labels[strength] || 'Unknown';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.sourceGoalId || !formData.targetGoalId) {
      setError('Please select both source and target goals');
      return;
    }

    if (formData.sourceGoalId === formData.targetGoalId) {
      setError('Source and target goals cannot be the same');
      return;
    }

    try {
      setLoading(true);
      setError('');

      await apiService.createDependency({
        sourceGoalId: formData.sourceGoalId,
        targetGoalId: formData.targetGoalId,
        dependencyType: formData.dependencyType,
        strength: formData.strength,
        description: formData.description.trim() || undefined,
        impactPercentage: formData.impactPercentage
      });

      onSuccess();
    } catch (err: any) {
      console.error('Failed to create dependency:', err);
      setError(err.response?.data?.error || 'Failed to create dependency');
    } finally {
      setLoading(false);
    }
  };

  const selectedSourceGoal = availableGoals.find(g => g.id === formData.sourceGoalId);
  const selectedTargetGoal = availableGoals.find(g => g.id === formData.targetGoalId);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              Create Dependency
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Goal Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Source Goal
                </label>
                <select
                  value={formData.sourceGoalId}
                  onChange={(e) => setFormData({ ...formData, sourceGoalId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select source goal...</option>
                  {Object.entries(goalsByProject).map(([projectName, goals]) => (
                    <optgroup key={projectName} label={projectName}>
                      {goals.map((goal) => (
                        <option key={goal.id} value={goal.id}>
                          {'  '.repeat(goal.level)} {goal.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {selectedSourceGoal && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-md text-sm">
                    <div className="font-medium text-gray-900">{selectedSourceGoal.label}</div>
                    <div className="text-gray-600">{selectedSourceGoal.projectName}</div>
                    <div className="text-gray-500">Level {selectedSourceGoal.level} • {Math.round(selectedSourceGoal.progress)}% complete</div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Goal
                </label>
                <select
                  value={formData.targetGoalId}
                  onChange={(e) => setFormData({ ...formData, targetGoalId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select target goal...</option>
                  {Object.entries(goalsByProject).map(([projectName, goals]) => (
                    <optgroup key={projectName} label={projectName}>
                      {goals.map((goal) => (
                        <option key={goal.id} value={goal.id}>
                          {'  '.repeat(goal.level)} {goal.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {selectedTargetGoal && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-md text-sm">
                    <div className="font-medium text-gray-900">{selectedTargetGoal.label}</div>
                    <div className="text-gray-600">{selectedTargetGoal.projectName}</div>
                    <div className="text-gray-500">Level {selectedTargetGoal.level} • {Math.round(selectedTargetGoal.progress)}% complete</div>
                  </div>
                )}
              </div>
            </div>

            {/* Dependency Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Dependency Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'blocks', label: 'Blocks', color: 'border-red-300 hover:border-red-500' },
                  { value: 'enables', label: 'Enables', color: 'border-green-300 hover:border-green-500' },
                  { value: 'reduces_complexity', label: 'Reduces Complexity', color: 'border-yellow-300 hover:border-yellow-500' },
                  { value: 'accelerates', label: 'Accelerates', color: 'border-blue-300 hover:border-blue-500' }
                ].map((type) => (
                  <label
                    key={type.value}
                    className={`relative flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.dependencyType === type.value
                        ? type.color.replace('hover:', '') + ' bg-opacity-10'
                        : 'border-gray-200 ' + type.color
                    }`}
                  >
                    <input
                      type="radio"
                      name="dependencyType"
                      value={type.value}
                      checked={formData.dependencyType === type.value}
                      onChange={(e) => setFormData({ ...formData, dependencyType: e.target.value as any })}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-3">
                      {getDependencyIcon(type.value)}
                      <div>
                        <div className="font-medium text-gray-900">{type.label}</div>
                        <div className="text-sm text-gray-600">
                          {getDependencyDescription(type.value)}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Strength and Impact */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Strength (1-5)
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={formData.strength}
                  onChange={(e) => setFormData({ ...formData, strength: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Weak</span>
                  <span className="font-medium">{getStrengthLabel(formData.strength)}</span>
                  <span>Strong</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Impact Percentage
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.impactPercentage}
                  onChange={(e) => setFormData({ ...formData, impactPercentage: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="text-xs text-gray-500 mt-1">
                  How much this dependency affects the target goal
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe the nature of this dependency..."
              />
            </div>

            {/* Preview */}
            {formData.sourceGoalId && formData.targetGoalId && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  {getDependencyIcon(formData.dependencyType)}
                  Dependency Preview
                </h4>
                <div className="text-sm text-blue-800">
                  <span className="font-medium">{selectedSourceGoal?.label}</span>
                  {' '}
                  <span className="lowercase">{formData.dependencyType.replace('_', ' ')}</span>
                  {' '}
                  <span className="font-medium">{selectedTargetGoal?.label}</span>
                  {' '}
                  with <span className="font-medium">{getStrengthLabel(formData.strength).toLowerCase()}</span> strength
                  and <span className="font-medium">{formData.impactPercentage}%</span> impact.
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.sourceGoalId || !formData.targetGoalId}
                className="btn-primary"
              >
                {loading ? 'Creating...' : 'Create Dependency'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

